import { useNewItems } from '../../../lib/sectionNewItems'
import { NewItemDot } from '../NewItemDot'
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import { createPortal } from 'react-dom'
import { useI18n, translate } from '../../../lib/i18n'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { SectionRefresh } from '../../SectionRefresh'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { deleteSubmission, getAccountUsage, getSubmission, listProjectForms, listSubmissions, setSubmissionStatus, type BillingPlan, type FormSubmission } from '../../../lib/api'
import { downloadCsv } from '../../../lib/csv'

/** Cuánto se lleva consumido del cupo mensual de mensajes del plan.
 *  `resetAt` es opcional porque el servidor no siempre lo manda. */
type FormsUsage = { used: number; limit: number | null; resetAt?: string | undefined }
import { contactIdForSubmission } from './ContactsSection'
import { Pagination, useDebounced, useServerPaged } from '../Pagination'
import { PlanUsageSummary } from '../PlanUsageSummary'
import { markDashboardItemsSeen } from '../../../lib/dashboardUnread'
import { readStarredIds, setStarred as setStarredIds } from '../../../lib/dashboardStars'
import { HeaderRefreshButton, ListToolbar, SelectCheckbox, StarButton, useListSelection } from '../ListSelection'
import { StarFilterButton } from '../StarFilterButton'
import { ConfirmDialog } from '../ConfirmDialog'
import { useToast } from '../../Toast'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import { IconAdjustmentsHorizontal, IconAlertTriangle, IconArrowLeft, IconCalendarEvent, IconCheck, IconChevronDown, IconClipboardList, IconCopy, IconDots, IconDownload, IconExternalLink, IconEye, IconInbox, IconLayoutGrid, IconLock, IconArrowBackUp, IconMail, IconMailOpened, IconMessage2, IconPhone, IconSearch, IconSquareCheck, IconStar, IconTrash, IconTypography, IconUser, IconX } from '@tabler/icons-react'
import { Notice } from '../Notice'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

/** Filas por página. El servidor manda como mucho 100. */
const PAGE_SIZE = 10
/** El tope del servidor, que es lo que se pide al filtrar por destacados. */
const SERVER_MAX_PAGE_SIZE = 100

function cleanValue(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function fieldLabel(key: string): string {
  const label = key.replace(/^_+/, '').replace(/[_-]+/g, ' ').trim()
  return label ? label.charAt(0).toLocaleUpperCase() + label.slice(1) : 'Respuesta'
}

function answers(submission: FormSubmission): Array<[string, string]> {
  return Object.entries(submission.data)
    .filter(([key, value]) => !key.startsWith('_wb_') && cleanValue(value))
    .map(([key, value]) => [fieldLabel(key), cleanValue(value)])
}

/**
 * Icono por campo, limitado a los tipos que el editor ofrece de verdad:
 * texto, correo, teléfono, párrafo, lista y casilla.
 *
 * El envío guarda sólo pares clave/valor de texto — el tipo no viaja con él —
 * así que hay que deducirlo. Lo que sí se puede afirmar se afirma; el resto cae
 * en "texto", que es el tipo por defecto del editor y no promete nada.
 *
 * Lista y texto comparten icono a propósito: nada en lo que guardamos los
 * distingue, y usar el de lista por el nombre del campo sería inventarlo.
 */
function fieldIcon(label: string, value: string) {
  const key = label.toLocaleLowerCase()
  const size = 13
  // Una casilla marcada llega como "on": es lo único que manda un checkbox.
  if (value === 'on' || value === 'true') return <IconSquareCheck size={size} />
  if (/mail|correo/.test(key) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return <IconMail size={size} />
  if (/phone|tel|celular|whatsapp|movil|móvil/.test(key)) return <IconPhone size={size} />
  // Un salto de línea sólo puede venir de un textarea.
  if (value.includes('\n') || value.length > 120) return <IconMessage2 size={size} />
  return <IconTypography size={size} />
}

function preview(submission: FormSubmission): string {
  return answers(submission)
    .map(([, value]) => value)
    .filter((value) => value !== submission.name && value !== submission.email)
    .join(' · ') || translate('forms.noExtraAnswers')
}

function dateTime(iso: string): string {
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(iso))
}

/**
 * El estado, en singular y siempre con texto.
 *
 * Los filtros de la lista dicen «Nuevos» y «Leídos» —hablan de montones— y
 * `statusLabel` deja en blanco lo leído porque en pantalla el silencio ya
 * significa atendido. En una hoja de cálculo una celda vacía no significa nada,
 * así que aquí cada fila lleva su palabra.
 */
const CSV_STATUS_KEY: Record<FormSubmission['status'], string> = {
  unread: 'forms.csv.statusUnread',
  read: 'forms.csv.statusRead',
  spam: 'forms.csv.statusSpam',
}

function statusLabel(status: FormSubmission['status']): string {
  if (status === 'unread') return 'Nuevo'
  if (status === 'spam') return 'Spam'
  return ''
}

/**
 * Los mismos nombres que entiende el worker. Cada columna ordenable tiene su
 * par ascendente/descendente; `name` a secas se mantiene porque es lo que
 * guardaban las versiones anteriores.
 */
type SubmissionSort =
  | 'recent' | 'oldest'
  | 'name' | 'nameAsc' | 'nameDesc'
  | 'formAsc' | 'formDesc'
  | 'statusAsc' | 'statusDesc'

export function FormsSection({ projectId, token, plan, timezone, onOpenPlans, onOpenContact, initialSubmissionId, onConsumeInitialSubmission, onRecordChange }: {
  projectId: string
  token: string
  plan: BillingPlan
  timezone: string
  onOpenPlans: () => void
  /** Abre la ficha del contacto que envió este mensaje. */
  onOpenContact?: ((contactId: string) => void) | undefined
  /** Mensaje a abrir al entrar, cuando se llega desde otra sección. */
  initialSubmissionId?: string | null | undefined
  onConsumeInitialSubmission?: (() => void) | undefined
  /** Refleja en la URL el mensaje abierto, para que "atrás" vuelva a la bandeja. */
  onRecordChange?: ((record: string | null) => void) | undefined
}) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('forms.searchPlaceholder')
  const { showToast } = useToast()
  const [query, setQuery] = useState('')
  const [formFilter, setFormFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | FormSubmission['status']>('all')
  const [sort, setSort] = useState<SubmissionSort>('recent')
  const [filterMenuOpen, setFilterMenuOpen] = useState(false)
  /**
   * «Sólo destacados» filtra sobre lo que hay cargado, no sobre la base.
   *
   * Los destacados viven en el navegador (ver [[dashboardStars]]), así que el
   * servidor no puede filtrarlos: pedirle la página 1 de 3 y quedarse con los
   * marcados dejaría fuera los de las otras dos. Con el filtro puesto se pide
   * la página grande que el servidor admite y se recorta aquí, y la paginación
   * refleja el resultado ya filtrado.
   */
  const [starredOnly, setStarredOnly] = useState(false)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  /** Posición del menú en el viewport, fuera de las capas de la tabla. */
  const [listMenu, setListMenu] = useState<{ top: number; left: number; up: boolean; maxHeight: number }>({ top: 0, left: 0, up: false, maxHeight: 0 })
  const [selectedId, setSelectedId] = useState(initialSubmissionId ?? '')

  // Mismo cierre que los menús de fila de Páginas y CMS: fuera con un clic o
  // con Escape. Se compara contra la envoltura entera y no contra el botón,
  // porque el menú se despliega dentro de ella y pulsar una opción no debe
  // contar como salir antes de que la opción llegue a ejecutarse.
  useEffect(() => {
    if (!menuOpenId) return
    function onDown(event: PointerEvent) {
      const el = event.target as Element | null
      if (!el?.closest?.('.dash-project-row-menu-wrap') && !el?.closest?.('.dash-form-row-menu-portal')) setMenuOpenId(null)
    }
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') setMenuOpenId(null) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [menuOpenId])

  // Igual que en contactos: la URL decide qué está abierto, y "sin nada abierto"
  // es una respuesta válida — es lo que hace que "atrás" cierre la ficha.
  useEffect(() => {
    setSelectedId(initialSubmissionId ?? '')
  }, [initialSubmissionId])

  /**
   * Avisar de que el id temporal ya se usó va en su propio efecto.
   *
   * El callback llega como función nueva en cada render del taller, así que
   * tenerlo entre las dependencias del efecto de arriba hacía que ese efecto se
   * repitiera constantemente — y con él `setSelectedId`. Cualquier render en el
   * que el id aún no hubiera llegado devolvía la pantalla a la bandeja.
   */
  useEffect(() => {
    if (initialSubmissionId) onConsumeInitialSubmission?.()
  }, [initialSubmissionId, onConsumeInitialSubmission])
  const [formUsage, setFormUsage] = useState<FormsUsage | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const filterMenuRef = useRef<HTMLDivElement>(null)
  /** El mensaje que se ha devuelto a «no leído» sin cerrarlo. Ver el efecto. */
  const keptUnreadIdRef = useRef<string | null>(null)

  const [paginationTotal, setPaginationTotal] = useState(0)
  const [starredTotal, setStarredTotal] = useState(0)
  const [formNames, setFormNames] = useState<string[]>([])
  const [selectedRow, setSelectedRow] = useState<FormSubmission | null>(null)
  const [submissionToDelete, setSubmissionToDelete] = useState<FormSubmission | null>(null)
  /** Borrado en bloque: se confirma aparte porque el texto habla en plural. */
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const debouncedQuery = useDebounced(query)
  /* Sigue visible mientras se despide, aunque el mensaje ya haya llegado. */
  const messageLoader = useRecordLoader(Boolean(selectedId) && !selectedRow)
  const [starred, setStarred] = useState<Set<string>>(() => readStarredIds(projectId, 'forms'))
  const paged = useServerPaged(starredOnly ? starredTotal : paginationTotal, starredOnly ? SERVER_MAX_PAGE_SIZE : PAGE_SIZE, `${debouncedQuery}|${formFilter}|${statusFilter}|${sort}|${starredOnly}`)
  const { page } = paged
  const listQuery = {
    page: starredOnly ? 1 : page + 1,
    pageSize: starredOnly ? SERVER_MAX_PAGE_SIZE : PAGE_SIZE,
    q: debouncedQuery, form: formFilter, status: statusFilter, sort,
  }
  const result = useSectionResource(token, [projectId, 'forms', listQuery], () => listSubmissions(projectId, token, listQuery))
  const submissions = useMemo(() => result.data?.data ?? [], [result.data])
  const total = result.data?.total ?? 0
  const hiddenOverLimit = result.data?.hiddenOverLimit ?? 0
  const loading = result.refreshing
  const failed = Boolean(result.error)
  const isNewSubmission = useNewItems('forms', submissions, !result.loading && !loading && !failed)
  const listLoader = useRecordLoader(result.loading)
  const updateResult = result.setData
  const hasResult = result.data !== undefined
  const setSubmissions = useCallback((update: SetStateAction<FormSubmission[]>) => {
    if (!hasResult) return
    updateResult((previous) => ({ ...previous, data: typeof update === 'function' ? update(previous.data) : update }))
  }, [hasResult, updateResult])
  const setTotal = (update: SetStateAction<number>) => {
    if (!result.data) return
    result.setData((previous) => ({ ...previous, total: typeof update === 'function' ? update(previous.total) : update }))
  }
  useEffect(() => {
    if (!result.data) return
    setPaginationTotal(total)
    setStarredTotal(submissions.reduce((count, row) => count + (starred.has(row.id) ? 1 : 0), 0))
  }, [result.data, starred, total, submissions])

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar. Aquí la lista la recorta el
     SERVIDOR, así que vacía puede significar «no hay» o «no encontró»:
     mientras haya algo escrito o algún filtro puesto la barra se queda, o
     no habría cómo deshacer la búsqueda que dejó la pantalla en blanco. */
  const showToolbar = submissions.length > 0
    || query.trim() !== ''
    || formFilter !== ''
    || statusFilter !== 'all'
    || starredOnly

  // Los nombres del desplegable salen de los formularios del proyecto, no de la
  // página que se esté viendo: si no, filtrar sólo ofrecería lo ya cargado.
  useEffect(() => {
    let alive = true
    void listProjectForms(projectId, token)
      .then((forms) => { if (alive) setFormNames([...new Set(forms.map((form) => form.name))]) })
      .catch((error: unknown) => console.error('[forms] no se pudieron cargar los formularios:', error))
    return () => { alive = false }
  }, [projectId, token])

  /** El enlace puede apuntar a un mensaje que no está en esta página. */
  useEffect(() => {
    if (!selectedId) { setSelectedRow(null); return }
    const known = submissions.find((row) => row.id === selectedId)
    if (known) { setSelectedRow(known); return }
    let alive = true
    void getSubmission(projectId, selectedId, token)
      .then((row) => { if (alive) setSelectedRow(row) })
      .catch(() => {
        // Borrado, o de otro proyecto. Sin esto la pantalla de carga se quedaba
        // puesta para siempre, porque `selectedId` sigue apuntando a algo.
        if (!alive) return
        setSelectedRow(null)
        setSelectedId('')
        onRecordChange?.(null)
      })
    return () => { alive = false }
  }, [selectedId, submissions, projectId, token, onRecordChange])

  /**
   * Un mensaje abierto es un mensaje leído.
   *
   * Cuelga de la ficha ya cargada y no de la pulsación en la fila porque a un
   * mensaje se llega por dos caminos: desde esta lista y desde un enlace —el
   * widget del panel, o la propia URL al volver atrás—. Marcándolo sólo en el
   * primero, quien abría un envío desde el panel y luego entraba a Formularios
   * se lo encontraba todavía con el rótulo de «Nuevo», que es justo lo que
   * acababa de leer. El widget ya lo daba por visto en este navegador, pero eso
   * no es el estado del envío: eso vive en el servidor.
   *
   * No se repite en bucle: al pasar a `read` deja de cumplirse la condición.
   *
   * La excepción es «marcar como no leído» con la ficha delante: ahí la orden
   * es explícita y gana sobre el automatismo, que si no la desharía en el mismo
   * parpadeo. Vale sólo mientras ese mensaje siga abierto — al cerrarlo, volver
   * a entrar es volver a leerlo.
   */
  useEffect(() => { keptUnreadIdRef.current = null }, [selectedId])

  useEffect(() => {
    const row = selectedRow
    if (!row) return
    markDashboardItemsSeen(projectId, 'forms', [row.id])
    if (row.status !== 'unread' || keptUnreadIdRef.current === row.id) return
    setSelectedRow((current) => current && current.id === row.id ? { ...current, status: 'read' as const } : current)
    setSubmissions((rows) => rows.map((item) => item.id === row.id ? { ...item, status: 'read' as const } : item))
    void setSubmissionStatus(projectId, row.id, 'read', token).catch(() => {})
  }, [selectedRow, projectId, token, setSubmissions])

  useEffect(() => {
    let alive = true
    void getAccountUsage(token)
      .then((usage) => {
        if (!alive) return
        setFormUsage({
          used: usage.used.formSubmissions,
          limit: usage.limits.form_submissions,
          resetAt: usage.formSubmissionsResetAt,
        })
      })
      .catch((error: unknown) => {
        /* Sin esto, un fallo aquí se enseñaba como «0 de ∞»: `formUsage` se
           quedaba nulo y un límite nulo se pinta igual que «sin límite». O sea
           que una cuenta gratis parecía no tener tope, que es lo contrario de
           lo que pasa. Un error tiene que verse como un error. */
        console.error('[forms] no se pudo leer el consumo de la cuenta:', error)
      })
    return () => { alive = false }
  }, [projectId, token])

  useEffect(() => {
    if (!filterMenuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!filterMenuRef.current?.contains(event.target as Node)) setFilterMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterMenuOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [filterMenuOpen])

  // `visible` ya no filtra nada: lo que llega del servidor es exactamente la
  // página pedida. Se mantiene el nombre para no tocar el resto del componente.
  const selected = selectedRow
  const visible = starredOnly ? submissions.filter((row) => starred.has(row.id)) : submissions
  const listSelection = useListSelection(visible.map((row) => row.id))

  const toggleStar = (ids: string[], next: boolean) => {
    setStarred(new Set(setStarredIds(projectId, 'forms', ids, next)))
  }

  /** Cambia el estado de varios de golpe y deja la selección limpia. */
  const bulkStatus = (next: FormSubmission['status']) => {
    const targets = visible.filter((row) => listSelection.isSelected(row.id) && row.status !== next)
    listSelection.clear()
    if (targets.length === 0) return
    for (const row of targets) changeStatus(row, next)
  }

  /**
   * Abrir un mensaje es lo que lo marca como visto — no entrar a la bandeja.
   *
   * Antes bastaba con asomarse a la lista para que el punto rojo desapareciera
   * de todos a la vez, incluidos los que no habías leído. El aviso existe para
   * decirte que hay algo por atender; si se apaga al pasar por delante, deja de
   * significar nada.
   */
  /**
   * Qué cuenta como filtro puesto, y cómo se quitan. En un solo sitio.
   *
   * Esto vivía escrito tres veces —el punto del botón, el «Restablecer» del
   * desplegable y el «Limpiar filtros» del estado vacío— y se separaron al
   * añadir «Sólo destacados»: el del estado vacío no lo tocaba, así que
   * pulsarlo con ese filtro puesto no cambiaba nada, y era precisamente el
   * filtro que dejaba la lista sin nada que enseñar.
   */
  const hasFilters = Boolean(query) || Boolean(formFilter) || statusFilter !== 'all' || sort !== 'recent' || starredOnly
  const clearFilters = () => {
    setQuery('')
    setFormFilter('')
    setStatusFilter('all')
    setSort('recent')
    setStarredOnly(false)
  }

  /**
   * Pulsar una cabecera ordena por esa columna; volver a pulsarla invierte.
   *
   * La fecha empieza por lo más reciente y el resto por A–Z, que es lo que se
   * espera de cada una: nadie abre una bandeja queriendo ver primero el mensaje
   * más viejo.
   */
  const toggleSort = (column: 'contact' | 'form' | 'status' | 'date') => {
    if (column === 'contact') setSort((current) => (current === 'nameAsc' || current === 'name' ? 'nameDesc' : 'nameAsc'))
    if (column === 'form') setSort((current) => (current === 'formAsc' ? 'formDesc' : 'formAsc'))
    if (column === 'status') setSort((current) => (current === 'statusAsc' ? 'statusDesc' : 'statusAsc'))
    if (column === 'date') setSort((current) => (current === 'recent' ? 'oldest' : 'recent'))
  }

  const openSubmission = (submission: FormSubmission) => {
    setSelectedId(submission.id)
    onRecordChange?.(submission.id)
  }

  /**
   * Cambia el estado de un envío. Lo usan la ficha y el menú de la fila.
   *
   * Se pinta antes de preguntar al servidor —marcar como leído no es una
   * operación que merezca una espera— pero se deshace si contesta que no: dejar
   * la fila diciendo lo contrario de lo que hay guardado es peor que no haber
   * cambiado nada, y antes ese fallo se tragaba en silencio.
   */
  const changeStatus = (submission: FormSubmission, next: FormSubmission['status']) => {
    if (submission.status === next) return
    setMenuOpenId(null)
    // Devolverlo a no leído es una orden, no un descuido: mientras siga abierto,
    // el automatismo de «abierto = leído» no vuelve a tocarlo.
    if (next === 'unread') keptUnreadIdRef.current = submission.id
    else if (keptUnreadIdRef.current === submission.id) keptUnreadIdRef.current = null
    setSubmissions((rows) => rows.map((row) => row.id === submission.id ? { ...row, status: next } : row))
    // La ficha se toca aparte: cuando se ha llegado a ella por un enlace, el
    // envío no está en la página cargada y no hay fila de la que copiar.
    setSelectedRow((current) => current && current.id === submission.id ? { ...current, status: next } : current)
    void setSubmissionStatus(projectId, submission.id, next, token).catch(() => {
      setSubmissions((rows) => rows.map((row) => row.id === submission.id ? { ...row, status: submission.status } : row))
      setSelectedRow((current) => current && current.id === submission.id ? { ...current, status: submission.status } : current)
      showToast(t('forms.statusFailed'), 'error')
    })
  }

  const removeSubmission = async (submission: FormSubmission) => {
    try {
      await deleteSubmission(projectId, submission.id, token)
      setSubmissions((rows) => rows.filter((row) => row.id !== submission.id))
      setTotal((current) => Math.max(0, current - 1))
      setFormUsage((current) => current ? { ...current, used: Math.max(0, current.used - 1) } : current)
      setSubmissionToDelete(null)
      setSelectedRow(null)
      setSelectedId('')
      onRecordChange?.(null)
      showToast(t('forms.deleted'))
    } catch (error) {
      showToast(t('forms.deleteFailed'), 'error')
      throw error
    }
  }

  /**
   * Borra los seleccionados.
   *
   * En secuencia y no en paralelo: no hay endpoint de borrado múltiple, y
   * lanzar veinte peticiones a la vez contra el mismo proyecto es la forma de
   * que el servidor empiece a rechazar algunas. Se quita de la lista lo que sí
   * se borró, y si algo falla se dice cuántos quedaron.
   */
  const removeSelected = async () => {
    const ids = listSelection.ids
    const done: string[] = []
    for (const id of ids) {
      try {
        await deleteSubmission(projectId, id, token)
        done.push(id)
      } catch {
        // Se sigue con el resto: parar a la primera dejaría media selección
        // borrada sin que nadie sepa cuál.
      }
    }
    const removed = new Set(done)
    setSubmissions((rows) => rows.filter((row) => !removed.has(row.id)))
    setTotal((current) => Math.max(0, current - done.length))
    setFormUsage((current) => current ? { ...current, used: Math.max(0, current.used - done.length) } : current)
    if (selectedRow && removed.has(selectedRow.id)) {
      setSelectedRow(null)
      setSelectedId('')
      onRecordChange?.(null)
    }
    listSelection.clear()
    setBulkDeleteOpen(false)
    if (done.length === ids.length) showToast(`${done.length} ${done.length === 1 ? 'mensaje borrado' : 'mensajes borrados'}`)
    else showToast(t('forms.bulkPartial', { done: done.length, total: ids.length }), 'error')
  }

  /**
   * Lo que se está viendo, a CSV.
   *
   * Con las respuestas ETIQUETADAS —«Teléfono: 555…»— y no sólo los valores
   * pegados: el resumen de la lista vale para reconocer un mensaje de un
   * vistazo, pero una hoja que dice «555… · Me interesa» sin decir qué era cada
   * cosa no sirve para nada. El estado va también, que es lo que distingue lo
   * pendiente de lo atendido y del spam.
   */
  const exportCsv = () => {
    downloadCsv(
      t('forms.csv.filename'),
      [
        t('forms.csv.date'),
        t('forms.csv.form'),
        t('forms.csv.name'),
        t('forms.csv.email'),
        t('forms.csv.status'),
        t('forms.csv.answers'),
        t('forms.csv.page'),
      ],
      visible.map((row) => [
        row.createdAt,
        row.formName,
        row.name,
        row.email,
        t(CSV_STATUS_KEY[row.status]),
        answers(row).map(([label, value]) => `${label}: ${value}`).join(' · '),
        row.pageUrl ?? '',
      ]),
    )
  }

  /**
   * Se llega aquí desde el panel con un mensaje ya elegido, y ese mensaje puede
   * no estar en la primera página, así que hay que pedirlo.
   *
   * Sin esta rama se caía a la bandeja durante ese instante: veías la lista
   * completa y acto seguido la ficha, como si la navegación se hubiera
   * equivocado de sitio y se corrigiera sola. La cabecera es la misma que la de
   * la ficha —mismo título de vuelta, misma posición— para que lo único que
   * cambie al llegar los datos sea el contenido.
   */
  /* La espera sustituye a la pantalla ENTERA: sin cabecera, sin barra y sin
     tabla, porque una pantalla a medio pintar mueve sus piezas cada vez que
     llega una. Ver `SectionLoader`. */
  if (selectedId && messageLoader.visible) return <SectionLoader loader={messageLoader} label={t('forms.loadingMessage')} />

  if (selected) {
    return (
      <div className="dash-projects-view dash-pages-view dash-form-record-view dash-loaded-content-in">
        <DashboardPageHeader
          title={selected.formName}
          subtitle={t('forms.sentBy', { name: selected.name || selected.email || t('forms.aVisitor'), date: dateTime(selected.createdAt) })}
          actions={<button className="dash-secondary-btn dash-detail-back" type="button" onClick={() => { setSelectedId(''); onRecordChange?.(null) }}><IconArrowLeft size={14} /> {t('forms.backToForms')}</button>}
        />
        {/* Misma ficha de dos columnas que un contacto: a la izquierda quién
            escribió y cuándo, a la derecha lo que escribió. */}
        <div className="dash-cp-grid">
          <aside className="dash-cp-side">
            <section className="dash-cp-card">
              <div className="dash-cp-card-head"><h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconClipboardList size={14} /></span> {t('forms.card.submission')}</h3></div>
              <div className="dash-cp-identity">
                <span className="dash-contact-avatar dash-contact-avatar--large"><IconUser size={18} /></span>
                <div>
                  <strong>{selected.name || t('overview.unnamedSubmission')}</strong>
                  <span>{selected.formName}</span>
                  {/* Aquí sin tope: hay sitio, y quien abre la ficha quiere el
                      detalle completo. */}
                  {(selected.formTags ?? []).length > 0 ? (
                    <span className="dash-form-tags">
                      {(selected.formTags ?? []).map((tag) => (
                        <span className="dash-form-tag" key={tag}>{tag}</span>
                      ))}
                    </span>
                  ) : null}
                </div>
              </div>
              <dl className="dash-cp-fields">
                <div>
                  <dt><IconMail size={13} /> {t('forms.detail.email')}</dt>
                  <dd className="dash-cp-copyable">
                    {selected.email ? (
                      <>
                        <a href={`mailto:${selected.email}`}>{selected.email}</a>
                        <button
                          type="button"
                          aria-label={t('forms.detail.copyEmailAria')}
                          data-tooltip="Copiar correo"
                          onClick={() => { void navigator.clipboard?.writeText(selected.email) }}
                        >
                          <IconCopy size={13} />
                        </button>
                      </>
                    ) : <em>{t('forms.detail.notProvided')}</em>}
                  </dd>
                </div>
                <div>
                  <dt><IconCalendarEvent size={13} /> {t('forms.detail.received')}</dt>
                  <dd>{dateTime(selected.createdAt)}</dd>
                </div>
                <div>
                  <dt><IconExternalLink size={13} /> {t('forms.detail.sourcePage')}</dt>
                  <dd className="dash-cp-copyable">
                    {selected.pageUrl ? (
                      <>
                        <a href={selected.pageUrl} target="_blank" rel="noreferrer" title={selected.pageUrl}>{selected.pageUrl}</a>
                        <button
                          type="button"
                          aria-label={t('forms.detail.copyUrlAria')}
                          data-tooltip="Copiar URL"
                          onClick={() => { void navigator.clipboard?.writeText(selected.pageUrl ?? '') }}
                        >
                          <IconCopy size={13} />
                        </button>
                      </>
                    ) : <em>{t('forms.detail.noSource')}</em>}
                  </dd>
                </div>
                {statusLabel(selected.status) ? (
                  <div>
                    <dt>{t('forms.detail.status')}</dt>
                    <dd><span className={`dash-form-submission-status is-${selected.status}`}>{statusLabel(selected.status)}</span></dd>
                  </div>
                ) : null}
              </dl>
            </section>

            {/* Marcar como nuevo es la única acción que cambia algo aquí; el
                resto de la ficha es lectura. */}
            <section className="dash-cp-card">
              <div className="dash-cp-card-head"><h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconAdjustmentsHorizontal size={14} /></span> {t('forms.card.actions')}</h3></div>
              <div className="dash-cp-actions">
                {onOpenContact ? (
                  <button
                    type="button"
                    className="dash-primary-btn"
                    onClick={() => onOpenContact(contactIdForSubmission(selected))}
                  >
                    <IconUser size={14} /> {t('forms.detail.openProfile')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="dash-secondary-btn"
                  onClick={() => changeStatus(selected, selected.status === 'unread' ? 'read' : 'unread')}
                >
                  {selected.status === 'unread' ? <><IconMailOpened size={14} /> {t('forms.detail.markRead')}</> : <><IconMail size={14} /> {t('forms.detail.markUnread')}</>}
                </button>
                {selected.email ? (
                  <a className="dash-secondary-btn" href={`mailto:${selected.email}`}><IconMail size={14} /> {t('forms.detail.reply')}</a>
                ) : null}
                <button
                  type="button"
                  className="dash-secondary-btn dash-cp-danger"
                  onClick={() => setSubmissionToDelete(selected)}
                >
                  <IconTrash size={14} /> {t('forms.detail.delete')}
                </button>
              </div>
            </section>
          </aside>

          <div className="dash-cp-main">
            <section className="dash-cp-card">
              <div className="dash-cp-card-head">
                <h3><span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-cp-head-icon is-fog" aria-hidden="true"><IconMessage2 size={14} /></span> {t('forms.card.answers')}</h3>
                <span className="dash-cp-count">{answers(selected).length}</span>
              </div>
              {answers(selected).length ? (
                <dl className="dash-cp-fields dash-cp-fields--roomy">
                  {answers(selected).map(([label, value], index) => (
                    <div key={`${label}-${index}`}>
                      <dt>{fieldIcon(label, value)} {label}</dt>
                      <dd className="dash-cp-answer">{value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="dash-cp-empty">
                  <IconClipboardList size={18} />
                  <p>{t('forms.detail.noAnswers')}</p>
                </div>
              )}
            </section>
          </div>
        </div>
        {submissionToDelete ? (
          <ConfirmDialog
            title={t('forms.detail.delete')}
            tone="danger"
            confirmLabel={t('forms.bulkDelete.confirm')}
            confirmingLabel="Borrando…"
            message={(
              <>
                {t('forms.delete.body')}
              </>
            )}
            onConfirm={() => removeSubmission(submissionToDelete)}
            onCancel={() => setSubmissionToDelete(null)}
          />
        ) : null}
        {bulkDeleteOpen ? (
          <ConfirmDialog
            title={listSelection.count === 1 ? t('forms.bulkDelete.titleOne') : t('forms.bulkDelete.titleMany', { count: listSelection.count })}
            tone="danger"
            confirmLabel={t('forms.bulkDelete.confirm')}
            confirmingLabel="Borrando…"
            message={t('forms.deleteWarning')}
            onConfirm={removeSelected}
            onCancel={() => setBulkDeleteOpen(false)}
          />
        ) : null}
      </div>
    )
  }

  /* La espera sustituye a la pantalla ENTERA: sin cabecera, sin barra y sin
     tabla, porque una pantalla a medio pintar mueve sus piezas cada vez que
     llega una. Ver `SectionLoader`. */
  if (listLoader.visible) return <SectionLoader loader={listLoader} label={t('forms.loadingList')} />

  return (
    <div className="dash-projects-view dash-pages-view dash-forms-list-view dash-loaded-content-in">
      <SectionRefresh refreshing={loading} failed={failed} />
      <DashboardPageHeader
        title={t('forms.title')}
        subtitle={t('forms.subtitle')}
        actionLayout="split"
        actions={(
          <>
            <HeaderRefreshButton refreshing={loading} onRefresh={() => { void result.refresh() }} />
            <PlanUsageSummary items={[{ label: t('forms.monthlySubmissions'), used: formUsage?.used ?? submissions.length, limit: formUsage?.limit ?? null, resetAt: formUsage?.resetAt, timeZone: timezone }]} plan={plan} onOpenPlans={onOpenPlans} />
          </>
        )}
      />

      {/* Lo que llegó pasado el tope del plan: guardado, pero sin leer hasta
          subir de plan. Sin este aviso la bandeja parecía decir que nadie había
          escrito, que es justo lo contrario de lo que pasó. */}
      {hiddenOverLimit > 0 ? (
        <Notice
          tone="warn"
          icon={<IconLock size={17} />}
          title={t('forms.overLimit.title')}
          actions={<button type="button" className="dash-primary-btn" onClick={onOpenPlans}>{t('forms.overLimit.cta')}</button>}
        >
          <p>{hiddenOverLimit === 1 ? t('forms.overLimit.bodyOne') : t('forms.overLimit.bodyMany', { count: hiddenOverLimit })}</p>
        </Notice>
      ) : null}

      {showToolbar ? (
        <header className="dash-toolbar dash-layered-toolbar">
          <div className="dash-toolbar-lead">
            <div className="dash-toolbar-search">
            <button type="button" className="dash-toolbar-search-icon" aria-label={t('forms.searchAria')} onClick={() => searchRef.current?.focus()}><IconSearch size={16} /></button>
            <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} />
            {query ? <button type="button" className="dash-toolbar-search-clear" aria-label={t('forms.clearSearch')} onClick={() => setQuery('')}><IconX size={13} /></button> : null}
            </div>
          </div>
          <div className="dash-toolbar-controls">
            <StarFilterButton active={starredOnly} onToggle={() => setStarredOnly((only) => !only)} label={t('forms.starred')} />
            <div className="dash-filter-wrap" ref={filterMenuRef}>
              <button className="dash-filter-trigger" type="button" aria-label={t('forms.filtersAria')} data-tooltip={t('forms.filtersAria')} aria-expanded={filterMenuOpen} onClick={() => setFilterMenuOpen((open) => !open)}>
                <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                {formFilter || statusFilter !== 'all' || sort !== 'recent' || starredOnly ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
              </button>
              {filterMenuOpen ? (
                <div className="dash-filter-popover" role="dialog" aria-label={t('forms.filtersDialogAria')}>
                  <div className="dash-filter-popover-head"><strong>{t('forms.filterTitle')}</strong><button type="button" aria-label={t('forms.closeFilters')} onClick={() => setFilterMenuOpen(false)}><IconX size={15} /></button></div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('forms.filterForm')}</span>
                    <label className="dash-filter-select">
                      <IconClipboardList size={14} aria-hidden="true" />
                      <select value={formFilter} aria-label={t('forms.filterByFormAria')} onChange={(event) => setFormFilter(event.target.value)}>
                        <option value="">{t('forms.allForms')}</option>
                        {formNames.map((name) => <option value={name} key={name}>{name}</option>)}
                      </select>
                      <IconChevronDown size={13} aria-hidden="true" />
                    </label>
                  </div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('forms.filterStarred')}</span>
                    <div className="dash-filter-options">
                      <button className={!starredOnly ? 'active' : ''} type="button" onClick={() => setStarredOnly(false)}><IconLayoutGrid size={14} /><span>{t('forms.status.all')}</span>{!starredOnly ? <IconCheck size={14} /> : null}</button>
                      <button className={starredOnly ? 'active' : ''} type="button" onClick={() => setStarredOnly(true)}><IconStar size={14} /><span>{t('forms.onlyStarred')}</span>{starredOnly ? <IconCheck size={14} /> : null}</button>
                    </div>
                  </div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('forms.filterStatus')}</span>
                    <div className="dash-filter-options">
                      {([
                        ['all', t('forms.status.all'), IconLayoutGrid],
                        ['unread', t('forms.status.unread'), IconMail],
                        ['read', t('forms.status.read'), IconMailOpened],
                        ['spam', 'Spam', IconAlertTriangle],
                      ] as const).map(([value, label, Icon]) => <button className={statusFilter === value ? 'active' : ''} type="button" onClick={() => setStatusFilter(value)} key={value}><Icon size={14} /><span>{label}</span>{statusFilter === value ? <IconCheck size={14} /> : null}</button>)}
                    </div>
                  </div>
                  {hasFilters ? <button className="dash-filter-reset" type="button" onClick={clearFilters}>{t('forms.resetFilters')}</button> : null}
                </div>
              ) : null}
            </div>
            {/* Sólo el dibujo, como destacados y filtros: es una acción más de la
                barra y no el mando principal de la pantalla, que es lo que decía
                siendo del tamaño de «Nueva página». El nombre vive en el globo y
                en `aria-label`. */}
            <button className="dash-filter-trigger" type="button" data-tooltip={t('forms.exportCsv')} aria-label={t('forms.exportCsvAria')} onClick={exportCsv} disabled={visible.length === 0}><IconDownload size={14} /></button>
          </div>
        </header>
      ) : null}

      {submissions.length > 0 ? (
        <ListToolbar
          selection={listSelection}
          actions={[
            { icon: <IconMailOpened size={15} />, label: t('forms.markRead'), onClick: () => bulkStatus('read') },
            { icon: <IconMail size={15} />, label: t('forms.markUnread'), onClick: () => bulkStatus('unread') },
            { icon: <IconStar size={14} />, label: 'Destacar', onClick: () => { toggleStar(listSelection.ids, true); listSelection.clear() } },
            { icon: <IconAlertTriangle size={14} />, label: 'Spam', onClick: () => bulkStatus('spam') },
            { icon: <IconTrash size={14} />, label: t('forms.bulkDelete.confirm'), danger: true, onClick: () => setBulkDeleteOpen(true) },
          ]}
        />
      ) : null}
      {/* Ya no hay hueco con forma de tabla mientras carga. Un esqueleto
          promete la silueta de lo que viene y evita el salto de altura, pero
          para eso tiene que estar rodeado de la pantalla de verdad —cabecera,
          buscador, paginación—, y eso es justo lo que se veía a medio hacer.
          Ahora la espera se lleva la pantalla entera, así que el hueco no tiene
          nada que sostener. */}
      <>
        {failed && result.data === undefined ? (
          <div className="dash-placeholder"><span>{t('forms.loadFailed')}</span></div>
        ) : submissions.length === 0 ? (
          /* Sin botón de crear, a diferencia de los demás vacíos: un envío no se
             hace desde aquí, lo escribe quien visita el sitio. */
          <div className="dash-placeholder">
            <IconClipboardList size={34} aria-hidden="true" />
            <strong>{t('forms.emptyTitle')}</strong>
            <span>{t('forms.emptyBody')}</span>
          </div>
        ) : visible.length === 0 ? (
          <div className="dash-placeholder">
            <IconSearch size={34} aria-hidden="true" />
            <strong>{t('forms.noResultsTitle')}</strong>
            <span>{t('forms.noResultsBody')}</span>
            <button className="dash-secondary-btn" type="button" onClick={clearFilters}>{t('forms.clearFilters')}</button>
          </div>
        ) : (
          <>
            <Pagination {...paged} onPage={paged.setPage} unit={t('forms.unit')} />
            <div className="dash-data-table dash-forms-data-table has-selection has-stars">
              <div className="dash-data-header" role="row">
                <span className="dash-data-select-cell">
                  <SelectCheckbox
                    checked={listSelection.allVisible}
                    mixed={listSelection.someVisible}
                    label={t(listSelection.allVisible ? 'list.deselectAll' : 'list.selectAllOnPage')}
                    onChange={listSelection.toggleAll}
                  />
                </span>
                <button className={sort === 'nameAsc' || sort === 'name' || sort === 'nameDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('contact')}>{t('forms.col.contact')} <IconChevronDown className={sort === 'nameAsc' || sort === 'name' ? 'is-asc' : ''} size={13} /></button>
                <button className={sort === 'formAsc' || sort === 'formDesc' ? 'is-active' : ''} type="button" onClick={() => toggleSort('form')}>{t('forms.col.form')} <IconChevronDown className={sort === 'formAsc' ? 'is-asc' : ''} size={13} /></button>
                <button className={sort === 'recent' || sort === 'oldest' ? 'is-active' : ''} type="button" onClick={() => toggleSort('date')}>{t('forms.col.date')} <IconChevronDown className={sort === 'oldest' ? 'is-asc' : ''} size={13} /></button>
                <span className="dash-data-header-actions">{t('forms.col.actions')}</span>
              </div>
              <div className="dash-project-list">
                {visible.map((submission: FormSubmission) => (
                  <article className={`dash-project-row dash-data-row dash-loaded-content-in${listSelection.isSelected(submission.id) ? ' is-selected' : ''}`} key={submission.id} onClick={openOnRowClick(() => openSubmission(submission))}>
                    <span className="dash-data-select-cell">
                      <SelectCheckbox
                        checked={listSelection.isSelected(submission.id)}
                        label={t('forms.selectMessage', { name: submission.name || submission.email || t('common.unnamed') })}
                        onChange={() => listSelection.toggle(submission.id)}
                      />
                      <StarButton
                        starred={starred.has(submission.id)}
                        label={t('forms.starMessageOf', { name: submission.name || submission.email || t('forms.noName') })}
                        onToggle={() => toggleStar([submission.id], !starred.has(submission.id))}
                      />
                    </span>
                    <div className="dash-project-row-main" role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter') openSubmission(submission) }}>
                      <span className="dash-project-row-thumb dash-form-row-icon dash-row-glass-fallback">
                        <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true"><IconClipboardList size={13} /></span>
                      </span>
                      <span className="dash-project-row-copy"><span className="dash-project-row-title">{isNewSubmission(submission) ? <NewItemDot /> : null}<strong>{submission.name || t('common.unnamed')}</strong>{statusLabel(submission.status) ? <span className={`dash-form-title-status is-${submission.status}`}>{statusLabel(submission.status)}</span> : null}</span><span className="dash-project-row-details">{submission.email || preview(submission)}</span></span>
                    </div>
                    <div className="dash-list-row-meta dash-form-row-meta">
                      <span className="dash-data-cell dash-form-source-cell" data-label="Formulario">
                        <span className="dash-form-source-name">{submission.formName}</span>
                        {/* Nombre y etiquetas pertenecen a la misma columna.
                            Como celdas hermanas, una etiqueta añadía una pista
                            que la tabla no tenía y mandaba las acciones a una
                            segunda fila. */}
                        {(submission.formTags ?? []).length > 0 ? (
                          <span className="dash-form-tags">
                            {(submission.formTags ?? []).slice(0, 3).map((tag) => (
                              <span className="dash-form-tag" key={tag}>{tag}</span>
                            ))}
                            {(submission.formTags ?? []).length > 3 ? (
                              <span className="dash-form-tag is-more" title={(submission.formTags ?? []).join(', ')}>
                                +{(submission.formTags ?? []).length - 3}
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                      </span>
                      <time className="dash-data-cell dash-list-date-cell" data-label="Fecha" dateTime={submission.createdAt}><IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />{formatDashboardListDate(submission.createdAt)}</time>
                    </div>
                    <div className="dash-project-row-actions">
                      <button className="dash-secondary-btn dash-project-row-action" type="button" aria-label={t('forms.viewForm')} onClick={() => openSubmission(submission)}>
                        <IconEye size={14} aria-hidden="true" />
                        <span className="dash-project-row-action-label">{t('common.open')}</span>
                      </button>
                      {/* Acciones rápidas sin abrir el mensaje. Las mismas que
                          ofrece la ficha, por el mismo `changeStatus`: repetir
                          aquí la lógica de estado habría dejado dos sitios que
                          pintan lo mismo y sólo uno que deshace el fallo. */}
                      <div className={`dash-project-row-menu-wrap${menuOpenId === submission.id ? ' is-open' : ''}${menuOpenId === submission.id && listMenu.up ? ' menu-up' : ''}`}>
                        <button
                          className="project-menu-btn dash-icon-btn dash-page-options-btn"
                          type="button"
                          aria-label={t('forms.moreOptions')}
                          data-tooltip="Opciones"
                          aria-expanded={menuOpenId === submission.id}
                          onClick={(event) => {
                            event.stopPropagation()
                            // Se mide al abrir: en las últimas filas de la lista
                            // no cabe hacia abajo y el menú se salía de la
                            // pantalla. Mismo cálculo que en Páginas.
                            if (menuOpenId !== submission.id) {
                              const rect = event.currentTarget.getBoundingClientRect()
                              const menuWidth = 184
                              const menuHeight = 234
                              const edge = 12
                              const gap = 8
                              const spaceBelow = window.innerHeight - rect.bottom - edge
                              const spaceAbove = rect.top - edge
                              const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                              const top = up
                                ? Math.max(edge, rect.top - menuHeight - gap)
                                : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                              const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                              setListMenu({ top, left, up, maxHeight: Math.max(160, up ? spaceAbove - gap : spaceBelow - gap) })
                            }
                            setMenuOpenId(menuOpenId === submission.id ? null : submission.id)
                          }}
                        >
                          <IconDots size={16} />
                        </button>
                        {menuOpenId === submission.id ? createPortal(
                          <div className={`project-menu dash-project-row-menu dash-form-row-menu-portal${listMenu.up ? ' is-up' : ''}`} role="menu" style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}>
                            <button
                              className="project-menu-item dash-mobile-project-menu-item"
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setMenuOpenId(null)
                                openSubmission(submission)
                              }}
                            >
                              <IconEye size={14} /> {t('forms.openForm')}
                            </button>
                            {submission.status === 'unread' ? (
                              <button className="project-menu-item" type="button" role="menuitem" onClick={() => changeStatus(submission, 'read')}><IconMailOpened size={14} /> {t('forms.detail.markRead')}</button>
                            ) : (
                              <button className="project-menu-item" type="button" role="menuitem" onClick={() => changeStatus(submission, 'unread')}><IconMail size={14} /> {t('forms.detail.markUnread')}</button>
                            )}
                            {submission.status === 'spam' ? (
                              <button className="project-menu-item" type="button" role="menuitem" onClick={() => changeStatus(submission, 'read')}><IconInbox size={14} /> {t('forms.unspam')}</button>
                            ) : (
                              <button className="project-menu-item" type="button" role="menuitem" onClick={() => changeStatus(submission, 'spam')}><IconAlertTriangle size={14} /> {t('forms.markSpam')}</button>
                            )}
                            {submission.email ? (
                              <>
                                <div className="project-menu-rule" role="separator" />
                                <button
                                  className="project-menu-item"
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setMenuOpenId(null)
                                    void navigator.clipboard.writeText(submission.email).then(
                                      () => showToast(t('forms.emailCopied')),
                                      () => showToast(t('forms.emailCopyFailed'), 'error'),
                                    )
                                  }}
                                >
                                  <IconCopy size={14} /> {t('forms.copyEmail')}
                                </button>
                                <a className="project-menu-item" role="menuitem" href={`mailto:${submission.email}`} onClick={() => setMenuOpenId(null)}><IconArrowBackUp size={14} /> {t('forms.detail.reply')}</a>
                              </>
                            ) : null}
                            <div className="project-menu-rule" role="separator" />
                            <button className="project-menu-item danger" type="button" role="menuitem" onClick={() => { setMenuOpenId(null); setSubmissionToDelete(submission) }}><IconTrash size={14} /> {t('forms.detail.delete')}</button>
                          </div>,
                          document.body,
                        ) : null}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          {/* Y al pie: quien acaba de recorrer la página busca el siguiente
              paso donde terminó de leer. Con una sola, sólo la cuenta. */}
          <Pagination {...paged} onPage={paged.setPage} unit={t('forms.unit')} alwaysShow />
          </>
        )}
      </>
    </div>
  )
}
